import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom-v5-compat";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import type { NavigationFlow } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  CoreHotelSteps,
  HOTELS_ROUTES,
  HOTELS_AVAILABILITY_PARAMS,
  HotelNavigationUtility,
  HOTELS_PATH,
} from "@overrides/utilities";
import {
  AvailabilityPageRoute,
  SearchPage,
  ShopPageRoute,
} from "@tenant/hotels";

export const useHotelsFlow = ({
  CheckoutPage,
}: {
  CheckoutPage: React.FC;
}): NavigationFlow<CoreHotelSteps> => {
  const { t } = useI18nContext();

  const selectedShopHotel = useSelector(HotelsShopSelectors.getSelectedHotel);
  const shopStayStartDate = useSelector(HotelsShopSelectors.getFromDate);
  const shopStayEndDate = useSelector(HotelsShopSelectors.getUntilDate);
  const guests = useSelector(HotelsShopSelectors.getGuests);
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  const [searchParams] = useSearchParams();

  const searchStep = useMemo(() => {
    return {
      [CoreHotelSteps.SEARCH]: {
        id: CoreHotelSteps.SEARCH,
        relativePath: HOTELS_ROUTES.SEARCH_ROUTE,
        component: <SearchPage />,
        show: true,
      },
    };
  }, []);

  const availabilityStep = useMemo(() => {
    return {
      [CoreHotelSteps.AVAILABILITY]: {
        id: CoreHotelSteps.AVAILABILITY,
        relativePath: `${HOTELS_ROUTES.AVAILABILITY_ROUTE}/${HOTELS_AVAILABILITY_PARAMS}`,
        component: <AvailabilityPageRoute />,
        show: true,
        breadcrumb: {
          label: t("core-hotels.breadcrumbs.availability"),
        },
      },
    };
  }, [t]);

  const shopStep = useMemo(() => {
    const lodgingName =
      selectedShopHotel?.lodging.name != null
        ? HotelNavigationUtility.placeLabelToUrl(selectedShopHotel.lodging.name)
        : "unknown";
    const lodgingId =
      selectedShopHotel?.lodging.id || searchParams.get("lodgingId");
    const relativePath = `${HOTELS_ROUTES.SHOP_ROUTE}/:lodgingName?/:lodgingId`;
    const params =
      shopStayStartDate != null && shopStayEndDate != null && guests != null
        ? HotelNavigationUtility.buildSearchParams({
            dateRange: { from: shopStayStartDate, until: shopStayEndDate },
            guests,
            roomsCount,
          })
        : searchParams;

    return {
      [CoreHotelSteps.SHOP]: {
        id: CoreHotelSteps.SHOP,
        relativePath,
        component: <ShopPageRoute />,
        show: true,
        queryParams: params.toString(),
        buildPath: () =>
          buildShopPath({
            relativePath,
            pathParams: { lodgingName, lodgingId },
          }),
        breadcrumb: {
          label: t("core-hotels.breadcrumbs.shop"),
        },
      },
    };
  }, [
    guests,
    roomsCount,
    selectedShopHotel?.lodging.id,
    selectedShopHotel?.lodging.name,
    shopStayEndDate,
    shopStayStartDate,
    t,
  ]);

  const bookStep = useMemo(() => {
    const lodgingId = selectedShopHotel?.lodging.id;

    const params =
      shopStayStartDate != null && shopStayEndDate != null && guests != null
        ? HotelNavigationUtility.buildSearchParams({
            dateRange: { from: shopStayStartDate, until: shopStayEndDate },
            guests,
            roomsCount,
          })
        : new URLSearchParams();

    if (lodgingId != null) params.append("lodgingId", lodgingId);

    return {
      [CoreHotelSteps.BOOK]: {
        id: CoreHotelSteps.BOOK,
        relativePath: HOTELS_ROUTES.BOOK_ROUTE,
        queryParams: params.toString(),
        component: <CheckoutPage />,
        show: true,
        breadcrumb: {
          label: t("core-hotels.breadcrumbs.book"),
        },
      },
    };
  }, [
    CheckoutPage,
    guests,
    roomsCount,
    selectedShopHotel?.lodging.id,
    shopStayEndDate,
    shopStayStartDate,
    t,
  ]);

  return useMemo(
    () => ({
      stepOrder: [
        CoreHotelSteps.SEARCH,
        CoreHotelSteps.AVAILABILITY,
        CoreHotelSteps.SHOP,
        CoreHotelSteps.BOOK,
      ],
      steps: {
        ...searchStep,
        ...availabilityStep,
        ...shopStep,
        ...bookStep,
      },
      funnelPath: HOTELS_PATH,
    }),
    [searchStep, availabilityStep, shopStep, bookStep]
  );
};

interface ShopPathParams {
  lodgingName?: string;
  lodgingId?: string;
}

interface ShopPathOptions {
  relativePath: string;
  pathParams: ShopPathParams;
}

const buildShopPath = ({ relativePath, pathParams }: ShopPathOptions): string =>
  relativePath
    .replace(":lodgingName?/", `${pathParams.lodgingName}/`)
    .replace(":lodgingId", pathParams.lodgingId ?? "");
